import React from 'react';
import ReactDom from 'react-dom';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { IGlyph, Icon } from '@treelab/panda';

import { TypographyStyle, TypographyWeight, ThemeStyle } from '../../../panda';

import { Typography } from '../../Typography';
import { FIELD_GLYPH_MAP, FIELD_ELEMENT_TYPE_MAP, FIELD_TYPE_VALIDATE } from '../../../constants/field';

import { IDispatch, IRootState } from '../../../modules/stores';
import { ElementTypes } from '../../../types/element';

import { ButtonContainer, SelectionSection, SelectionTitleMixin, OptionContainer } from './styled';
import { ColumnType } from '../../../modules/core/graphql/types';
import { getForeignTableIdByType } from '../../../helpers';

type IConnectProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
interface IElementPackage {
  componentId: string;
  elementType: ElementTypes;
  fieldId?: string;
  name: string;
  icon: IGlyph;
}
interface IState {
  // Item that user is about to create. User has begun to drag element item and is about to drop it somewhere.
  activeElementPackage: IElementPackage | null;
  offsetX: number;
  offsetY: number;
}

class SidebarOptions extends React.PureComponent<IConnectProps, IState> {
  public constructor(props: IConnectProps) {
    super(props);
    this.state = {
      activeElementPackage: null,
      offsetX: 0,
      offsetY: 0,
    };
  }

  public componentWillUpdate(_: IConnectProps, nextState: IState) {
    if (!!nextState.activeElementPackage && this.state.activeElementPackage === null) {
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    } else if (!nextState.activeElementPackage && !!this.state.activeElementPackage) {
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    }
  }

  public componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  public render() {
    const { fieldIds, fieldById, visibleNodes } = this.props;

    return (
      <>
        <SelectionSection>
          <SelectionTitle>
            <FormattedMessage id="sidebar.option.header.static" />
          </SelectionTitle>
          {this.renderPortal()}
          {this.renderOption({
            componentId: 'static-text',
            name: 'Text',
            icon: FIELD_GLYPH_MAP[ColumnType.TEXT],
            elementType: ElementTypes.StaticText,
          })}
          {this.renderOption({
            componentId: 'static-image',
            name: 'Image',
            icon: FIELD_GLYPH_MAP[ColumnType.MULTI_ATTACHMENT],
            elementType: ElementTypes.StaticAttachment,
          })}
        </SelectionSection>
        <SelectionSection>
          <SelectionTitle>
            <FormattedMessage id="sidebar.option.header.field" />
          </SelectionTitle>
          {fieldIds.map((fieldId: string) => {
            const field = fieldById[fieldId];
            if (!field) return null;

            if (
              field.type === ColumnType.RECORD_REFERENCE ||
              field.type === ColumnType.SUBTABLE ||
              (field.type === ColumnType.LOOKUP &&
                (field.typeOptions.lookupColumnType === ColumnType.RECORD_REFERENCE ||
                  field.typeOptions.lookupColumnType === ColumnType.SUBTABLE))
            ) {
              const tableId = getForeignTableIdByType(field);
              if (!visibleNodes[tableId]) return null;
            }
            // 如果列类型暂不支持，过滤该类型
            if(!FIELD_TYPE_VALIDATE[field.type]) return null;

            return this.renderOption({
              componentId: fieldId,
              name: field.name,
              icon: FIELD_GLYPH_MAP[field.type as ColumnType],
              elementType: FIELD_ELEMENT_TYPE_MAP[field.type as ColumnType]!,
              fieldId,
            });
          })}
        </SelectionSection>
        {/* <RemoveTemplate /> */}
      </>
    );
  }

  renderPortal = () => {
    const { activeElementPackage } = this.state;

    if (activeElementPackage === null) return null;

    const elToClone = document.getElementById(activeElementPackage.componentId);
    const clientRect = elToClone && elToClone.getBoundingClientRect();

    if (!clientRect) return null;

    return ReactDom.createPortal(
      <div
        style={{
          position: 'absolute',
          top: clientRect && clientRect.top,
          left: clientRect && clientRect.left,
          width: clientRect && clientRect.width,
          transform: `translate(${this.state.offsetX}px, ${this.state.offsetY}px)`,
        }}
      >
        <SelectionOption icon={activeElementPackage.icon} isClone>
          {activeElementPackage.name}
        </SelectionOption>
      </div>,
      document.body,
    );
  };

  renderOption = (args: IElementPackage) => (
    <OptionContainer
      onMouseDown={() => this.handleMouseDown(args)}
      id={args.componentId}
      key={args.componentId}
    >
      <SelectionOption icon={args.icon}>{args.name}</SelectionOption>
    </OptionContainer>
  );

  private handleMouseMove = (e: any) => {
    this.setState({
      offsetX: this.state.offsetX + e.movementX,
      offsetY: this.state.offsetY + e.movementY,
    });
  };

  private handleMouseUp = (e: any) => {
    const { activeElementPackage } = this.state;
    if (activeElementPackage === null) return;

    /**
     * On mouse up, user is dropping element and creating a new element. In order to do this,
     * we need to calculate the position of the drop, and the position of the canvas,
     * and also the position of the scroll.
     */
    try {
      const scrollContent = document.getElementById('content-scroll');
      const mainContent = document.getElementById('main-content');

      const posX = e.clientX - mainContent!.offsetLeft + scrollContent!.scrollLeft - 300;
      const posY = e.clientY - mainContent!.offsetTop + scrollContent!.scrollTop - 68;

      if (posX < 0 || posY < 0) {
        // Element was not dropped in canvas so we will put in a default pos
        this.props.createAndAddSpace({
          elementType: activeElementPackage.elementType,
          fieldId: activeElementPackage.fieldId,
        });
      } else {
        this.props.createAndAddSpace({
          elementType: activeElementPackage.elementType,
          position: { posX, posY },
          fieldId: activeElementPackage.fieldId,
        });
      }
    } catch (e) {
      console.warn("Couldn't drop element", e);
    }

    this.setState({ activeElementPackage: null, offsetX: 0, offsetY: 0 });
  };

  private handleMouseDown = (args: IElementPackage) => {
    this.setState({ activeElementPackage: args });
  };
}

interface ISelectionOptionProps {
  children: React.ReactNode;
  icon: IGlyph;
  isClone?: boolean;
}
const SelectionOption: React.FC<ISelectionOptionProps> = ({ children, icon, isClone = false }) => (
  <ButtonContainer isClone={isClone}>
    <Icon icon={icon} size={{ height: 20, width: 20 }}></Icon>
    <span
      className="text-ellipsis panda-typography--span2 panda-btn--text-left"
      style={{ marginLeft: '8px' }}
    >
      {children}
    </span>
  </ButtonContainer>
);

const SelectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    type={TypographyStyle.P2}
    weight={TypographyWeight.Regular}
    css={SelectionTitleMixin}
    theme={ThemeStyle.white}
  >
    {children}
  </Typography>
);

const mapState = ({ core }: IRootState) => ({
  fieldIds: core.viewById[core.currentViewId].fieldAllIds,
  fieldById: core.fieldById,
  visibleNodes: core.visibleNodes,
});

const mapDispatch = ({ space }: IDispatch) => ({
  createAndAddSpace: space.createAndAddSpace,
});

export default connect(
  mapState,
  // @ts-ignore
  mapDispatch,
)(SidebarOptions);
